import { Provider } from '@vgtv/api-client';

import {
  getAssetMainImage,
  getAssetTags,
  getBaseUrl,
  getSeoAssetTitle,
  getSeoAssetDescription,
  isStoryAsset,
  getStoryUrl,
  getAssetMetadata,
} from './assetHelpers.js';
import { getPath, routeNames } from './routes.js';
import { getHomepageSlug } from './getHomepageSlug.js';

import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { CnpStory } from '@vgtv/api-client/lib/cnp_story';
import type { CnpTag, TagEnrichment } from '@vgtv/api-client/lib/cnp_tag';
import type { SvpCategoryAdditional } from '@vgtv/api-client/lib/svp_category';
import type { Vendor } from '../../types.js';

function getDefaults(provider: Provider) {
  switch (provider) {
    case Provider.VGTV:
      return {
        robots: 'all',
        title: getSuffix(provider),
        keywords: '',
        description:
          'VGTV er Norges største web-tv-kanal. Nyheter, sport og underholdning.',
        canonical: getBaseUrl(provider),
        openGraph: {
          'og:site_name': getSuffix(provider),
          'og:title': getSuffix(provider),
          'og:description':
            'VGTV er Norges største web-tv-kanal. Nyheter, sport og underholdning.',
          'og:type': 'article',
          'og:url': getBaseUrl(provider),
          'og:image':
            'https://imbo.vgtv.no/users/vgtv/images/8e61b94a2f047894ec0c982a2feefe70?t[]=1280x720',
          'og:image:width': '1280',
          'og:image:height': '720',
        },
      };
    case Provider.AB:
      return {
        robots: 'all',
        title: getSuffix(provider),
        keywords: '',
        description:
          'Se de senaste klippen, programmen och följ alla livesändningar från Aftonbladet TV här.',
        canonical: getBaseUrl(provider),
        openGraph: {
          'og:site_name': getSuffix(provider),
          'og:title': getSuffix(provider),
          'og:description':
            'Se de senaste klippen, programmen och följ alla livesändningar från Aftonbladet TV här.',
          'og:type': 'article',
          'og:url': getBaseUrl(provider),
          'og:image':
            'https://tv.aftonbladet.se/images/abtv-og-image-a06c5a0a3af707b4669d0b9932c13e52.jpg',
          'og:image:width': '200',
          'og:image:height': '200',
        },
      };
  }
}

function getSuffix(provider: Provider): string {
  switch (provider) {
    case Provider.VGTV:
      return 'VGTV';
    case Provider.AB:
      return 'Aftonbladet TV';
  }
}

function addProviderSuffix(provider: Provider, text?: string): string {
  const suffix = getSuffix(provider);

  return text ? `${text} - ${suffix}` : suffix;
}

function getDefaultMetadata(provider: Provider) {
  return { ...getDefaults(provider) };
}

function getPaywalPageMetadata(provider: Provider) {
  const robots = 'noindex';
  return { ...getDefaults(provider), robots };
}

function getAssetPageMetadata(provider: Provider, asset: SvpAssetAdditional) {
  const defaults = getDefaults(provider);
  const baseUrl = getBaseUrl(provider);

  const tags = getAssetTags(asset);
  const title = addProviderSuffix(provider, getSeoAssetTitle(asset));
  const description = getSeoAssetDescription(asset);

  const isVGStory = Provider.VGTV && isStoryAsset(asset);

  const url = isVGStory
    ? getStoryUrl(asset)
    : `${baseUrl}${getPath(routeNames.asset, {
        asset,
        skipDeeplinkCheck: true,
      })}`;

  const showInNewest = asset.additional?.settings?.showInNewest;
  const seoNoIndex = getAssetMetadata(asset).seoNoIndex === 'true';

  const robots = showInNewest && !isVGStory && !seoNoIndex ? 'all' : 'noindex';

  return {
    ...defaults,
    title,
    robots,
    keywords: tags.map((tag) => tag.tag).join(','),
    description,
    canonical: url,
    openGraph: {
      ...defaults.openGraph,
      'og:title': title,
      'og:description': description,
      'og:type': 'video.other',
      'og:url': url,
      'og:image': `${getAssetMainImage(asset)}?t[]=1280x720`,
    },
  };
}

function getCategoryPageMetadata({
  provider,
  vendor,
  category,
  overrides = {},
}: {
  provider: Provider;
  vendor: Vendor;
  category: SvpCategoryAdditional;
  overrides?: {
    title?: string;
    description?: string;
    keywords?: string;
  };
}) {
  const defaults = getDefaults(provider);
  const baseUrl = getBaseUrl(provider);

  const title = addProviderSuffix(provider, overrides.title || category.title);
  const description =
    overrides.description ||
    category.additional.description ||
    defaults.description;

  const url = `${baseUrl}${getPath(routeNames.category, {
    category,
    vendor,
  })}`;

  return {
    ...defaults,
    title: title,
    keywords: overrides.keywords || '',
    description: description,
    canonical: url,
    openGraph: {
      ...defaults.openGraph,
      'og:title': title,
      'og:description': description,
      'og:type': 'article',
      'og:url': url,
      'og:image': `${category.additional.image}?t[]=1280x720`,
    },
  };
}

function getTagPageMetadata(
  provider: Provider,
  tag: CnpTag & {
    images?: TagEnrichment['images'];
  },
  overrides: {
    title?: string;
    description?: string;
    keywords?: string;
  } = {}
) {
  const defaults = getDefaults(provider);
  const baseUrl = getBaseUrl(provider);

  const title = addProviderSuffix(provider, overrides.title || tag.title);
  const description = overrides.description || defaults.description;

  const url = `${baseUrl}${getPath(routeNames.tag, {
    tagId: tag.id,
    title: tag.title,
  })}`;

  return {
    ...defaults,
    title: title,
    keywords: overrides.keywords || '',
    description: description,
    canonical: url,
    openGraph: {
      ...defaults.openGraph,
      'og:title': title,
      'og:description': description,
      'og:type': 'article',
      'og:url': url,
      'og:image': tag.images ? `${tag.images.main}?t[]=1280x720` : '',
    },
  };
}

function getStoryPageMetadata(
  provider: Provider,
  story: CnpStory,
  overrides: {
    title?: string;
    description?: string;
    keywords?: string;
  } = {}
) {
  const defaults = getDefaults(provider);
  const baseUrl = getBaseUrl(provider);

  const title = addProviderSuffix(provider, overrides.title || story.title);
  const description = overrides.description || defaults.description;

  const url = `${baseUrl}${getPath(routeNames.story, {
    storyId: story.id,
    title: story.title,
  })}`;

  return {
    ...defaults,
    title: title,
    keywords: overrides.keywords || '',
    description: description,
    canonical: url,
    openGraph: {
      ...defaults.openGraph,
      'og:title': title,
      'og:description': description,
      'og:type': 'article',
      'og:url': url,
    },
  };
}

function getCustomPageMetadata(
  provider: Provider,
  slug: string,
  overrides: {
    title?: string;
    description?: string;
    keywords?: string;
  } = {}
) {
  const defaults = getDefaults(provider);
  const baseUrl = getBaseUrl(provider);

  const title = overrides.title
    ? addProviderSuffix(provider, overrides.title)
    : defaults.title;
  const description = overrides.description || defaults.description;

  const isFrontpage = slug === getHomepageSlug(provider);

  const url = isFrontpage
    ? baseUrl
    : `${baseUrl}${getPath(routeNames.custom, {
        slug: slug,
      })}`;

  return {
    ...defaults,
    title: title,
    keywords: overrides.keywords || defaults.keywords,
    description: description,
    canonical: url,
    openGraph: {
      ...defaults.openGraph,
      'og:title': title,
      'og:description': description,
      'og:type': isFrontpage ? 'website' : 'article',
      'og:url': url,
      'og:image': defaults.openGraph['og:image'],
    },
  };
}

export {
  getDefaultMetadata,
  getAssetPageMetadata,
  getCategoryPageMetadata,
  getTagPageMetadata,
  getStoryPageMetadata,
  getCustomPageMetadata,
  getPaywalPageMetadata,
};
